import { useTheme } from '../../hooks/useTheme'
import { useState, useEffect, useRef } from 'react'

// Delicate embroidery leaf - filled satin stitch style like the reference
const EmbroideryLeaf = ({ x, y, size = 10, color, rotation = 0 }) => (
  <g 
    transform={`translate(${x}, ${y}) rotate(${rotation})`}
    className="embroidery-element"
  >
    {/* Leaf shape - satin stitch filled */}
    <path
      d={`M 0 0 
          Q ${size * 0.6} ${-size * 0.3} ${size * 0.4} ${-size} 
          Q 0 ${-size * 1.2} ${-size * 0.4} ${-size}
          Q ${-size * 0.6} ${-size * 0.3} 0 0`}
      fill={color}
      opacity="0.85"
    />
    {/* Center vein - darker line */}
    <path
      d={`M 0 ${-size * 0.15} L 0 ${-size * 0.85}`}
      stroke={color}
      strokeWidth="1"
      fill="none"
      opacity="0.4"
      style={{ filter: 'brightness(0.6)' }}
    />
  </g>
)

// Small berry/bud cluster
const BerryCluster = ({ x, y, color }) => (
  <g transform={`translate(${x}, ${y})`} className="embroidery-element">
    <circle cx={0} cy={0} r={2.5} fill={color} />
    <circle cx={-4} cy={-3} r={2} fill={color} opacity="0.85" />
    <circle cx={4} cy={-2} r={2} fill={color} opacity="0.85" />
    <circle cx={1} cy={-5} r={1.5} fill={color} opacity="0.7" />
  </g>
)

// Tiny embroidered flower - 5 petals like lazy daisy stitch
const TinyFlower = ({ x, y, size = 8, petalColor, centerColor = '#fcd34d' }) => (
  <g transform={`translate(${x}, ${y})`} className="embroidery-element">
    {/* Petals */}
    {[0, 72, 144, 216, 288].map((angle, i) => (
      <ellipse
        key={i}
        cx={0}
        cy={-size * 0.5}
        rx={size * 0.28}
        ry={size * 0.5}
        fill={petalColor}
        transform={`rotate(${angle})`}
        opacity="0.9"
      />
    ))}
    {/* Center - French knot */}
    <circle cx={0} cy={0} r={size * 0.22} fill={centerColor} />
  </g>
)

// Curving vine tendril
const VineTendril = ({ x, y, length = 25, angle = 0, color = '#22c55e' }) => {
  const curl = length * 0.4
  return (
    <g transform={`translate(${x}, ${y}) rotate(${angle})`} className="embroidery-element">
      <path
        d={`M 0 0 
            Q ${curl} ${-length * 0.3} ${length * 0.3} ${-length * 0.6}
            Q ${length * 0.5} ${-length * 0.8} ${length * 0.3} ${-length}`}
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
      />
    </g>
  )
}

// Simple vine stem segment - midpoint passed as prop to avoid re-render randomness
const VineStem = ({ startX, startY, endX, endY, midOffsetX = 0, midOffsetY = 0, color = '#22c55e' }) => {
  const midX = (startX + endX) / 2 + midOffsetX
  const midY = (startY + endY) / 2 + midOffsetY
  
  return (
    <path
      d={`M ${startX} ${startY} Q ${midX} ${midY} ${endX} ${endY}`}
      stroke={color}
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      className="embroidery-element"
    />
  )
}

// The sewing needle with thread
const SewingNeedle = ({ x, y, angle = 0, threadColor }) => (
  <g transform={`translate(${x}, ${y}) rotate(${angle})`}>
    {/* Trailing thread - gentle curve */}
    <path
      d={`M 0 18 
          Q -10 30 -5 42 
          Q 0 54 -12 60`}
      stroke={threadColor}
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      opacity="0.4"
    />
    
    {/* Needle body - silver/metallic */}
    <rect 
      x={-1.5} 
      y={-18} 
      width={3} 
      height={36} 
      rx={1.5} 
      fill="url(#needleMetal)" 
    />
    
    {/* Needle eye */}
    <ellipse cx={0} cy={-10} rx={1} ry={2.5} fill="#4b5563" />
    
    {/* Thread through eye */}
    <path
      d={`M -0.5 -12 Q -5 -16 -8 -14`}
      stroke={threadColor}
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
    
    {/* Needle tip highlight */}
    <ellipse cx={0} cy={16} rx={0.5} ry={2} fill="#ffffff" opacity="0.7" />
  </g>
)

const StitchBorder = () => {
  const { isDark } = useTheme()
  const [elements, setElements] = useState([])
  const [needlePos, setNeedlePos] = useState({ x: 30, y: 30, angle: 135 })
  const animationRef = useRef(null)
  const lastSpawnTime = useRef(0)
  const elementIdRef = useRef(0)
  
  // Beautiful floral colors - pinks, reds, blues, purples
  const flowerColors = [
    '#ec4899', // pink-500
    '#f472b6', // pink-400
    '#fb7185', // rose-400
    '#f43f5e', // rose-500
    '#ef4444', // red-500
    '#a855f7', // purple-500
    '#8b5cf6', // violet-500
    '#6366f1', // indigo-500
    '#3b82f6', // blue-500
    '#0ea5e9', // sky-500
  ]
  
  // Green colors for leaves and vines
  const leafColors = [
    '#22c55e', // green-500
    '#16a34a', // green-600
    '#4ade80', // green-400
    '#15803d', // green-700
    '#86efac', // green-300
  ]
  
  const vineColor = '#22c55e'
  const threadColor = isDark ? '#a78bfa' : '#7c3aed'

  // Get position along the rectangular border
  const getPositionOnBorder = (progress) => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1200
    const h = typeof window !== 'undefined' ? window.innerHeight : 800
    const padding = 30
    
    const topLen = w - 2 * padding
    const rightLen = h - 2 * padding
    const bottomLen = w - 2 * padding
    const leftLen = h - 2 * padding
    const total = topLen + rightLen + bottomLen + leftLen
    
    const dist = (progress % 1) * total
    
    if (dist < topLen) {
      return { x: padding + dist, y: padding, angle: 90, edge: 'top' }
    }
    if (dist < topLen + rightLen) {
      const d = dist - topLen
      return { x: w - padding, y: padding + d, angle: 180, edge: 'right' }
    }
    if (dist < topLen + rightLen + bottomLen) {
      const d = dist - topLen - rightLen
      return { x: w - padding - d, y: h - padding, angle: 270, edge: 'bottom' }
    }
    const d = dist - topLen - rightLen - bottomLen
    return { x: padding, y: h - padding - d, angle: 0, edge: 'left' }
  }

  // Spawn decorative element behind the needle
  const spawnElement = (x, y, edge) => {
    const id = ++elementIdRef.current
    const rand = Math.random()
    
    // Offset elements to the inside of the border
    let ox = 0, oy = 0
    const spread = 25
    const inward = 10 + Math.random() * 15
    
    if (edge === 'top') { oy = inward; ox = (Math.random() - 0.5) * spread }
    else if (edge === 'bottom') { oy = -inward; ox = (Math.random() - 0.5) * spread }
    else if (edge === 'left') { ox = inward; oy = (Math.random() - 0.5) * spread }
    else if (edge === 'right') { ox = -inward; oy = (Math.random() - 0.5) * spread }
    
    let el
    
    if (rand < 0.35) {
      // Leaf
      el = {
        id, type: 'leaf',
        x: x + ox, y: y + oy,
        color: leafColors[Math.floor(Math.random() * leafColors.length)],
        size: 8 + Math.random() * 7,
        rotation: Math.random() * 360,
      }
    } else if (rand < 0.55) {
      // Flower
      el = {
        id, type: 'flower',
        x: x + ox, y: y + oy,
        petalColor: flowerColors[Math.floor(Math.random() * flowerColors.length)],
        size: 7 + Math.random() * 5,
      }
    } else if (rand < 0.7) {
      // Berry cluster
      el = {
        id, type: 'berry',
        x: x + ox, y: y + oy,
        color: flowerColors[Math.floor(Math.random() * flowerColors.length)],
      }
    } else if (rand < 0.85) {
      // Vine tendril
      el = {
        id, type: 'tendril',
        x: x + ox * 0.5, y: y + oy * 0.5,
        length: 15 + Math.random() * 20,
        angle: Math.random() * 360,
        color: vineColor,
      }
    } else {
      // Vine stem - calculate random offsets at creation time
      const len = 20 + Math.random() * 25
      el = {
        id, type: 'stem',
        startX: x, startY: y,
        endX: x + ox + (Math.random() - 0.5) * len,
        endY: y + oy + (Math.random() - 0.5) * len,
        midOffsetX: (Math.random() - 0.5) * 15,
        midOffsetY: (Math.random() - 0.5) * 15,
        color: vineColor,
      }
    }
    
    setElements(prev => [...prev.slice(-60), el])
  }

  // Animation loop - VERY SLOW for zen peaceful feeling
  useEffect(() => {
    let startTime = Date.now()
    const cycleDuration = 90000 // 90 seconds per full loop - super slow and calming
    
    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = (elapsed % cycleDuration) / cycleDuration
      
      const pos = getPositionOnBorder(progress)
      setNeedlePos(pos)
      
      // Spawn element every 500ms
      const now = Date.now()
      if (now - lastSpawnTime.current > 500) {
        spawnElement(pos.x, pos.y, pos.edge)
        lastSpawnTime.current = now
      }
      
      animationRef.current = requestAnimationFrame(animate)
    }
    
    animate()
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current) }
  }, [isDark])

  const w = typeof window !== 'undefined' ? window.innerWidth : 1200
  const h = typeof window !== 'undefined' ? window.innerHeight : 800

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      <svg className="absolute inset-0 w-full h-full">
        <defs>
          {/* Needle metallic gradient */}
          <linearGradient id="needleMetal" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#94a3b8" />
            <stop offset="30%" stopColor="#cbd5e1" />
            <stop offset="50%" stopColor="#f1f5f9" />
            <stop offset="70%" stopColor="#cbd5e1" />
            <stop offset="100%" stopColor="#94a3b8" />
          </linearGradient>
        </defs>

        {/* Subtle dotted border guide */}
        <rect
          x="30" y="30"
          width={w - 60} height={h - 60}
          fill="none"
          stroke={isDark ? '#374151' : '#d1d5db'}
          strokeWidth="1"
          strokeDasharray="3 6"
          opacity="0.3"
          rx="3"
        />

        {/* All embroidered elements */}
        {elements.map(el => {
          switch (el.type) {
            case 'leaf':
              return <EmbroideryLeaf key={el.id} x={el.x} y={el.y} size={el.size} color={el.color} rotation={el.rotation} />
            case 'flower':
              return <TinyFlower key={el.id} x={el.x} y={el.y} size={el.size} petalColor={el.petalColor} />
            case 'berry':
              return <BerryCluster key={el.id} x={el.x} y={el.y} color={el.color} />
            case 'tendril':
              return <VineTendril key={el.id} x={el.x} y={el.y} length={el.length} angle={el.angle} color={el.color} />
            case 'stem':
              return <VineStem key={el.id} startX={el.startX} startY={el.startY} endX={el.endX} endY={el.endY} midOffsetX={el.midOffsetX} midOffsetY={el.midOffsetY} color={el.color} />
            default:
              return null
          }
        })}

        {/* Corner flowers - always present */}
        <TinyFlower x={45} y={45} size={14} petalColor="#ec4899" />
        <EmbroideryLeaf x={60} y={40} size={10} color="#22c55e" rotation={45} />
        <EmbroideryLeaf x={40} y={60} size={9} color="#16a34a" rotation={-30} />
        
        <TinyFlower x={w - 45} y={45} size={14} petalColor="#8b5cf6" />
        <EmbroideryLeaf x={w - 60} y={40} size={10} color="#22c55e" rotation={135} />
        <EmbroideryLeaf x={w - 40} y={60} size={9} color="#4ade80" rotation={-150} />
        
        <TinyFlower x={45} y={h - 45} size={14} petalColor="#3b82f6" />
        <EmbroideryLeaf x={60} y={h - 40} size={10} color="#16a34a" rotation={30} />
        <EmbroideryLeaf x={40} y={h - 60} size={9} color="#22c55e" rotation={-45} />
        
        <TinyFlower x={w - 45} y={h - 45} size={14} petalColor="#f43f5e" />
        <EmbroideryLeaf x={w - 60} y={h - 40} size={10} color="#4ade80" rotation={150} />
        <EmbroideryLeaf x={w - 40} y={h - 60} size={9} color="#22c55e" rotation={-135} />

        {/* The needle */}
        <SewingNeedle 
          x={needlePos.x} 
          y={needlePos.y} 
          angle={needlePos.angle}
          threadColor={threadColor}
        />
      </svg>

      {/* Elements are more subtle/faint so content pops */}
      <style>{`
        .embroidery-element {
          opacity: 0.35;
        }
      `}</style>
    </div>
  )
}

export default StitchBorder